<template>
  <div class="container">
    <div class="card">
      <div class="card-title">
        <div class="d-flex align-center justify-space-between">
          <div class="d-flex align-center">
            <i class="icon mdi mdi-chat"></i>
            <h2 class="font-weight-bold text-h6 ml-2">Chat Gestión Total</h2>
          </div>

          <button @click="leaveRoom" class="btn btn-primary text-capitalize">
            Salir {{ room || '' }}
          </button>
        </div>
      </div>

      <hr>

      <div class="card-body">
        <div class="d-flex">
          <div class="sidebar">
            <div class="room-info">
              <div class="room-name">
                <i class="icon mdi mdi-chat-outline"></i>
                <span class="ml-2">Sala</span>
              </div>
              <p class="current-room">{{ currentRoom || 'Sin sala' }}</p>
              <small :class="connected ? 'status-ok' : 'status-bad'">
                {{ connected ? 'Conectado' : 'Desconectado' }}
              </small>
            </div>

            <div class="users-list-title">
              <i class="icon mdi mdi-account-group-outline"></i>
              <span class="ml-2">Usuarios</span>
            </div>

            <ul class="users-list">
              <li v-for="user in users" :key="user.client_id || user.id">
                {{ user.username }}
              </li>
            </ul>
          </div>

          <div class="chat-box">
            <div
              v-for="(chat, i) in chats"
              :key="i"
              class="chat-message"
              :class="{
                'admin-message': chat.username === 'Gestion Total',
                'user-message': chat.username === username,
                'other-user-message': chat.username !== username && chat.username !== 'Gestion Total'
              }"
            >
              <div class="message-header">
                <span class="chat-username">{{ chat.username }}</span>
                <span class="chat-time">{{ chat.time }}</span>
              </div>
              <p class="chat-text">{{ chat.text }}</p>
            </div>
          </div>
        </div>
      </div>

      <hr>

      <div class="card-footer">
        <form @submit.prevent="onSubmit">
          <input
            v-model="message"
            type="text"
            placeholder="Escribe un mensaje"
            class="input-message"
          />
          <button type="submit" class="btn-send" :disabled="!connected">Enviar</button>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { getOrConnectSocket } from '@/services/websocket';

export default {
  name: 'ChatRoom',
  setup() {
    const store = useStore();
    const router = useRouter();

    const message = ref('');
    const chats = ref([]);
    const users = ref([]);
    const connected = ref(false);
    const socketRef = ref(null);

    const username = computed(() => store.state.user?.name || store.state.user?.email || 'Guest');

    const room = computed(() => {
      if (store.state.context?.branch?.token) return `branch_${store.state.context.branch.token}`;
      if (store.state.context?.company?.token) return `company_${store.state.context.company.token}`;
      return store.state.user?.token || '';
    });

    const currentRoom = computed(() => room.value);

    const handleRoomUsers = ({ room: payloadRoom, users: roomUsers = [] } = {}) => {
      if (payloadRoom && payloadRoom !== currentRoom.value) return;
      users.value = roomUsers;
    };

    const handleMessage = (msg) => {
      chats.value.push(msg);
    };

    const handleConnect = () => {
      connected.value = true;
    };

    const handleDisconnect = () => {
      connected.value = false;
      users.value = [];
    };

    const bindSocket = () => {
      const socket = getOrConnectSocket(store.state.context, store.state.user);
      socketRef.value = socket;

      if (!socket) return;

      connected.value = socket.connected;

      socket.off('roomUsers', handleRoomUsers);
      socket.off('message', handleMessage);
      socket.off('connect', handleConnect);
      socket.off('disconnect', handleDisconnect);

      socket.on('roomUsers', handleRoomUsers);
      socket.on('message', handleMessage);
      socket.on('connect', handleConnect);
      socket.on('disconnect', handleDisconnect);
    };

    const onSubmit = () => {
      const text = message.value.trim();
      if (!text) return;

      const socket = socketRef.value || getOrConnectSocket(store.state.context, store.state.user);

      if (socket?.connected) {
        socket.emit('chatMessage', text);
        message.value = '';
      }
    };

    const leaveRoom = () => {
      router.push('/dashboard');
    };

    onMounted(bindSocket);

    onBeforeUnmount(() => {
      const socket = socketRef.value;
      if (!socket) return;

      socket.off('roomUsers', handleRoomUsers);
      socket.off('message', handleMessage);
      socket.off('connect', handleConnect);
      socket.off('disconnect', handleDisconnect);
    });

    return {
      message,
      chats,
      users,
      connected,
      room,
      currentRoom,
      onSubmit,
      leaveRoom,
      username,
    };
  },
};
</script>

<style scoped>
.d-flex { display: flex; }
.align-center { align-items: center; }
.justify-space-between { justify-content: space-between; width: 100%; }
.container { display: flex; justify-content: center; padding: 20px; }
.card { width: 100%; max-width: 800px; background-color: #fff; border-radius: 8px; box-shadow: 0 2px 4px rgba(0,0,0,0.1); }
.card-title { display: flex; justify-content: space-between; align-items: center; padding: 20px; }
.card-body { display: flex; width: 100%; min-width: 800px; flex-wrap: nowrap; }
.card-body .d-flex { width: 100%; min-height: 600px; display: flex; flex-wrap: wrap; }
.sidebar { width: 30%; height: 100%; background-color: #f4f4f4; padding: 20px; }
.room-info { margin-bottom: 20px; }
.room-name { display: flex; align-items: center; }
.current-room { word-break: break-all; }
.status-ok { color: #15803d; font-weight: 700; }
.status-bad { color: #b91c1c; font-weight: 700; }
.users-list-title { margin-top: 20px; }
.users-list { list-style: none; padding: 0; }
.users-list li { padding: 10px 0; }
.chat-box { flex-grow: 1; width: 70%; overflow-y: auto; padding: 20px; }
.chat-message { margin-bottom: 10px; border-radius: 8px; padding: 10px; max-width: 50%; }
.admin-message { background-color: #fce4ec; margin-right: auto; margin-left: auto; }
.user-message { background-color: #e3f2fd; margin-left: auto; }
.other-user-message { background-color: #c8e6c9; margin-right: auto; }
.message-header { display: flex; justify-content: space-between; }
.chat-username { font-weight: bold; }
.chat-time { font-size: 12px; }
.input-message { width: 80%; padding: 10px; border: 1px solid #ddd; border-radius: 4px; margin-right: 10px; }
.btn-send { padding: 10px 20px; background-color: #1976d2; color: white; border: none; border-radius: 4px; cursor: pointer; }
.btn-send:disabled { opacity: .5; cursor: not-allowed; }
.btn-send:hover:not(:disabled) { background-color: #1565c0; }
.btn-primary { background-color: #1976d2; color: white; padding: 10px 20px; border: none; cursor: pointer; }
.btn-primary:hover { background-color: #1565c0; }
.icon { font-size: 24px; }
.text-h6 { font-size: 18px; font-weight: bold; }
.text-capitalize { text-transform: capitalize; }
.font-weight-bold { font-weight: bold; }
.text-primary { color: #1976d2; }
</style>
